import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';
import TrafficAnalytics from './TrafficAnalytics';
import ProductMetrics from './ProductMetrics';
import { TrendingUp, Target, BarChart3, User, Star, Share2, MessageSquare } from 'lucide-react';

const PROXY = process.env.REACT_APP_PROXY_URL || 'https://primary-lens-production.up.railway.app';

const C = {
  bg:      '#f9fafb',
  surface: '#ffffff',
  border:  '#e5e7eb',
  text:    '#111827',
  muted:   '#6b7280',
  accent:  '#3b82f6',
  green:   '#10b981',
  purple:  '#8b5cf6',
  orange:  '#f59e0b',
  red:     '#ef4444',
};

// ── shared helpers ─────────────────────────────────────────────────────────
const TIP = { contentStyle: { fontSize: 12 }, itemStyle: { fontSize: 12 } };
const fmtDuration = s => {
  if (!s && s !== 0) return '—';
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};
const fmtTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

function KpiCard({ label, value, sub }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: '20px 24px', flex: 1, minWidth: 140,
    }}>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: C.text }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Event badge ────────────────────────────────────────────────────────────
const EVENT_COLORS = {
  session_start:     { bg: '#dbeafe', text: '#1d4ed8' },
  session_end:       { bg: '#f3f4f6', text: '#6b7280' },
  page_view:         { bg: '#d1fae5', text: '#065f46' },
  project_analyzed:  { bg: '#ede9fe', text: '#5b21b6' },
  watchlist_add:     { bg: '#fef3c7', text: '#92400e' },
  watchlist_remove:  { bg: '#fee2e2', text: '#991b1b' },
  share_screenshot:  { bg: '#fce7f3', text: '#9d174d' },
  note_added:        { bg: '#ecfdf5', text: '#064e3b' },
  compare_viewed:    { bg: '#f0fdf4', text: '#166534' },
  import_project:    { bg: '#fff7ed', text: '#9a3412' },
};

function EventBadge({ event }) {
  const col = EVENT_COLORS[event] || { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      background: col.bg, color: col.text,
      fontSize: 10, fontWeight: 600, borderRadius: 4,
      padding: '2px 6px', whiteSpace: 'nowrap',
    }}>{event}</span>
  );
}

// ── Single session row (expandable) ───────────────────────────────────────
function SessionRow({ session }) {
  const [open, setOpen] = useState(false);
  const isLoggedIn = Boolean(session.user_id);
  const analyzeCount = session.events.filter(e => e.event === 'project_analyzed').length;
  const hasShare = session.events.some(e => e.event === 'share_screenshot');
  const hasWatchlist = session.events.some(e => e.event === 'watchlist_add');

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      {/* Row header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'grid',
          gridTemplateColumns: '80px 90px 80px 80px 100px 80px 60px 60px 60px auto',
          gap: 8, padding: '10px 16px', cursor: 'pointer',
          background: open ? '#f0f9ff' : 'transparent',
          alignItems: 'center',
          fontSize: 12, color: C.text,
        }}
      >
        <span style={{ color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>
          {session.session_id}
        </span>
        <span>{fmtTime(session.started_at)}</span>
        <span>{fmtDuration(session.duration_s)}</span>
        <span>{session.device || '—'}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>{session.tz || session.region || '—'}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>{session.referrer || '—'}</span>
        <span style={{
          color: isLoggedIn ? C.green : C.muted,
          fontWeight: isLoggedIn ? 600 : 400,
        }}>{isLoggedIn ? '已登录' : '匿名'}</span>
        <span style={{ color: analyzeCount > 0 ? C.purple : C.muted }}>
          {analyzeCount > 0 ? `分析×${analyzeCount}` : '—'}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {hasWatchlist && <Star size={12} strokeWidth={2} fill={C.orange} color={C.orange} />}
          {hasShare && <Share2 size={12} strokeWidth={2} color={C.accent} />}
        </span>
        <span style={{ color: C.accent, fontSize: 11 }}>{open ? '▲ 收起' : '▼ 展开'}</span>
      </div>

      {/* Event timeline */}
      {open && (
        <div style={{ padding: '12px 16px 16px 32px', background: '#f8fafc' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {session.events.map((ev, i) => {
              const props = ev.properties || {};
              const detail = ev.event === 'project_analyzed' ? ` → ${props.project_name || '?'}`
                : ev.event === 'page_view'        ? ` → ${props.page || '?'}`
                : ev.event === 'session_end'      ? ` (${fmtDuration(props.duration_s)})`
                : ev.event === 'watchlist_add'    ? ` → ${props.project_name || '?'}`
                : ev.event === 'share_screenshot' ? ` → ${props.project_name || '?'}`
                : ev.event === 'import_project'   ? ` → ${props.project_name || '?'} (${props.source || ''})`
                : '';
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: C.muted, fontSize: 10, fontFamily: 'monospace', minWidth: 40 }}>
                    {new Date(ev.created_at).toLocaleTimeString('zh', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <EventBadge event={ev.event} />
                  <span style={{ fontSize: 11, color: C.muted }}>{detail}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Visitor Sessions View ─────────────────────────────────────────────────
function VisitorSessionsView({ userId, accessToken }) {
  const [sessions, setSessions] = useState(null);
  const [error, setError]       = useState(null);
  const [days, setDays]         = useState(7);
  const [filter, setFilter]     = useState('all'); // all | anon | loggedin | analyzed
  const [search, setSearch]     = useState('');

  const load = useCallback(() => {
    setSessions(null);
    setError(null);
    fetch(`${PROXY}/admin/visitor-sessions?uid=${userId}&days=${days}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setSessions(d.sessions || []);
      })
      .catch(e => setError(e.message));
  }, [userId, days, accessToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = (sessions || []).filter(s => {
    if (filter === 'anon'      && s.user_id)  return false;
    if (filter === 'loggedin'  && !s.user_id) return false;
    if (filter === 'analyzed'  && !s.events.some(e => e.event === 'project_analyzed')) return false;
    if (search) {
      const q = search.toLowerCase();
      const hit = s.session_id.includes(q)
        || (s.tz || '').toLowerCase().includes(q)
        || (s.referrer || '').toLowerCase().includes(q)
        || s.events.some(e => JSON.stringify(e.properties || {}).toLowerCase().includes(q));
      if (!hit) return false;
    }
    return true;
  });

  const btnStyle = (active) => ({
    padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: active ? 600 : 400,
    background: active ? C.accent : C.surface,
    color: active ? '#fff' : C.text,
    border: `1px solid ${active ? C.accent : C.border}`,
  });

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: C.muted }}>时间范围：</span>
        {[1, 3, 7, 14].map(d => (
          <button key={d} style={btnStyle(days === d)} onClick={() => setDays(d)}>
            近 {d} 天
          </button>
        ))}
        <span style={{ fontSize: 13, color: C.muted, marginLeft: 8 }}>筛选：</span>
        {[['all','全部'],['anon','匿名'],['loggedin','已登录'],['analyzed','有分析']].map(([v, l]) => (
          <button key={v} style={btnStyle(filter === v)} onClick={() => setFilter(v)}>{l}</button>
        ))}
        <input
          placeholder="搜索时区、来源、项目名…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            marginLeft: 'auto', padding: '6px 12px', borderRadius: 6, fontSize: 12,
            border: `1px solid ${C.border}`, outline: 'none', minWidth: 200,
          }}
        />
      </div>

      {error && <div style={{ color: C.red, padding: 16 }}>Error: {error}</div>}
      {!sessions && !error && <div style={{ color: C.muted, padding: 16 }}>加载中…</div>}

      {sessions && (
        <>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
            共 {sessions.length} 个会话，显示 {filtered.length} 条
          </div>

          {/* Table */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '80px 90px 80px 80px 100px 80px 60px 60px 60px auto',
              gap: 8, padding: '10px 16px',
              background: C.bg, fontSize: 11, fontWeight: 600, color: C.muted,
              borderBottom: `1px solid ${C.border}`,
            }}>
              <span>Session ID</span>
              <span>开始时间</span>
              <span>时长</span>
              <span>设备</span>
              <span>时区</span>
              <span>来源</span>
              <span>状态</span>
              <span>行为</span>
              <span>操作</span>
              <span></span>
            </div>

            {filtered.length === 0
              ? <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>无匹配数据</div>
              : filtered.map(s => <SessionRow key={s.session_id} session={s} />)
            }
          </div>
        </>
      )}
    </div>
  );
}

// ── Stats View (original dashboard) ───────────────────────────────────────
function StatsView({ userId, accessToken }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${PROXY}/admin/analytics?uid=${userId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(e => setError(e.message));
  }, [userId, accessToken]);

  if (error) return <div style={{ padding: 40, color: C.red }}>Error: {error}</div>;
  if (!data)  return <div style={{ padding: 40, color: C.muted }}>Loading analytics…</div>;

  const {
    kpis, dau, topProjects, funnel, tabUsage, scoreDist, avgDims, feedbackAccuracy, feedbackWeak,
    visitorKpis = {}, deviceBreakdown = [], regionBreakdown = [], tzBreakdown = [],
    referrerBreakdown = [], durationDist = [],
  } = data;

  return (
    <div>
      {/* KPIs */}
      <Section title="核心指标">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <KpiCard label="本周会话"    value={kpis.sessions_7d} />
          <KpiCard label="月度会话"    value={kpis.sessions_30d} />
          <KpiCard label="本周分析次数" value={kpis.analyses_7d} />
          <KpiCard label="月度分析次数" value={kpis.analyses_30d} />
          <KpiCard label="注册用户"    value={kpis.users_total} />
          <KpiCard label="缓存项目数"  value={kpis.projects_total} />
        </div>
      </Section>

      {/* Visitor KPIs */}
      <Section title="访客概览">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <KpiCard label="总访问会话" value={visitorKpis.total_sessions} />
          <KpiCard label="平均访问时长" value={visitorKpis.avg_duration_s ? fmtDuration(visitorKpis.avg_duration_s) : '—'} />
          <KpiCard label="已登录会话" value={visitorKpis.logged_in_sessions} />
          <KpiCard label="匿名会话"   value={visitorKpis.anon_sessions} sub="未登录访客" />
        </div>
      </Section>

      {/* DAU */}
      <Section title="每日活跃 (DAU)">
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dau} {...TIP}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip {...TIP} />
              <Line type="monotone" dataKey="sessions" stroke={C.accent} dot={false} name="会话数" />
              <Line type="monotone" dataKey="analyses" stroke={C.green}  dot={false} name="分析次数" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>Top 10 分析项目</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {topProjects.length === 0
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无数据</div>
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={topProjects} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                    <Tooltip {...TIP} />
                    <Bar dataKey="count" fill={C.accent} name="分析次数" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>用户行为漏斗</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funnel}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="step" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip {...TIP} />
                <Bar dataKey="count" fill={C.purple} name="次数" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>页面浏览分布</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {tabUsage.length === 0
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无数据</div>
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={tabUsage}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="page" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip {...TIP} />
                    <Bar dataKey="count" fill={C.orange} name="浏览次数" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>评分分布</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={scoreDist}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip {...TIP} />
                <Bar dataKey="count" fill={C.green} name="项目数" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>平均维度评分</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={avgDims}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="dim" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip {...TIP} />
                <Bar dataKey="avg" fill={C.accent} name="平均分" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>用户反馈</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {feedbackAccuracy.every(f => f.count === 0)
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无反馈数据</div>
              : (
                <>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>评分准确度</div>
                  <ResponsiveContainer width="100%" height={90}>
                    <BarChart data={feedbackAccuracy} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="label" type="category" tick={{ fontSize: 10 }} width={60} />
                      <Tooltip {...TIP} />
                      <Bar dataKey="count" fill={C.purple} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  {feedbackWeak.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: C.muted, margin: '12px 0 8px' }}>最弱维度反馈</div>
                      <ResponsiveContainer width="100%" height={80}>
                        <BarChart data={feedbackWeak} layout="vertical">
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis dataKey="dim" type="category" tick={{ fontSize: 10 }} width={70} />
                          <Tooltip {...TIP} />
                          <Bar dataKey="count" fill={C.orange} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  )}
                </>
              )}
          </div>
        </div>
      </div>

      {/* Visitor analytics */}
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: '8px 0 20px',
        borderTop: `2px solid ${C.border}`, paddingTop: 24 }}>访客行为分析</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>设备类型</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {deviceBreakdown.length === 0
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无数据（访问后积累）</div>
              : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={deviceBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="device" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip {...TIP} />
                    <Bar dataKey="count" fill={C.accent} name="会话数" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>流量来源渠道</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {referrerBreakdown.length === 0
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无数据</div>
              : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={referrerBreakdown} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="referrer" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip {...TIP} />
                    <Bar dataKey="count" fill={C.green} name="访问数" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>访问地区分布</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {regionBreakdown.length === 0
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无数据</div>
              : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={regionBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="region" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip {...TIP} />
                    <Bar dataKey="count" fill={C.purple} name="会话数" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>访问时长分布</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            {durationDist.every(d => d.count === 0)
              ? <div style={{ color: C.muted, fontSize: 13 }}>暂无数据（session_end 积累中）</div>
              : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={durationDist}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip {...TIP} />
                    <Bar dataKey="count" fill={C.orange} name="会话数" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>
      </div>

      {tzBreakdown.length > 0 && (
        <Section title="细粒度时区分布（Top 10）">
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 16px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={tzBreakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="tz" type="category" tick={{ fontSize: 10 }} width={140} />
                <Tooltip {...TIP} />
                <Bar dataKey="count" fill={C.accent} name="会话数" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}
    </div>
  );
}

// ── Feedback View ──────────────────────────────────────────────────────────
const FEEDBACK_TYPE_LABELS = { bug: 'Bug', idea: '建议', other: '其他' };
const FEEDBACK_TYPE_COLORS = {
  bug:   { bg: '#fee2e2', text: '#991b1b' },
  idea:  { bg: '#fef3c7', text: '#92400e' },
  other: { bg: '#f3f4f6', text: '#374151' },
};

function FeedbackView({ userId, accessToken }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${PROXY}/admin/feedback?uid=${userId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setItems(d.feedback || []); })
      .catch(e => setError(e.message));
  }, [userId, accessToken]);

  if (error) return <div style={{ padding: 40, color: C.red }}>Error: {error}</div>;
  if (!items) return <div style={{ padding: 40, color: C.muted }}>加载中…</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>共 {items.length} 条反馈</div>
      {items.length === 0
        ? <div style={{ color: C.muted, padding: 24 }}>暂无反馈</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(f => {
              const col = FEEDBACK_TYPE_COLORS[f.type] || FEEDBACK_TYPE_COLORS.other;
              return (
                <div key={f.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ background: col.bg, color: col.text, fontSize: 11, fontWeight: 600, borderRadius: 4, padding: '2px 8px' }}>
                      {FEEDBACK_TYPE_LABELS[f.type] || f.type}
                    </span>
                    <span style={{ fontSize: 11, color: C.muted }}>{fmtTime(f.created_at)}</span>
                    {f.email && <span style={{ fontSize: 11, color: C.muted }}>· {f.email}</span>}
                    {f.page && <span style={{ fontSize: 11, color: C.muted }}>· {f.page}{f.project_name ? `/${f.project_name}` : ''}</span>}
                    {f.lang && <span style={{ fontSize: 11, color: C.muted }}>· {f.lang}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap' }}>{f.message}</div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ── Root: tab switcher ─────────────────────────────────────────────────────
export default function AdminDashboard({ userId, accessToken }) {
  const [tab, setTab] = useState('traffic'); // traffic | stats | sessions

  const tabBtn = (id, label) => (
    <button
      onClick={() => setTab(id)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 20px', fontSize: 13, fontWeight: tab === id ? 700 : 400,
        borderRadius: '8px 8px 0 0', cursor: 'pointer',
        background: tab === id ? C.surface : 'transparent',
        color: tab === id ? C.accent : C.muted,
        border: `1px solid ${C.border}`,
        borderBottom: tab === id ? `1px solid ${C.surface}` : `1px solid ${C.border}`,
        marginBottom: -1,
      }}
    >{label}</button>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.bg, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>
        数据看板
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}` }}>
        {tabBtn('traffic',  <><TrendingUp size={13} strokeWidth={2} /> 流量分析</>)}
        {tabBtn('product',  <><Target size={13} strokeWidth={2} /> 产品指标</>)}
        {tabBtn('stats',    <><BarChart3 size={13} strokeWidth={2} /> 统计总览</>)}
        {tabBtn('sessions', <><User size={13} strokeWidth={2} /> 访客明细</>)}
        {tabBtn('feedback', <><MessageSquare size={13} strokeWidth={2} /> 用户反馈</>)}
      </div>

      {tab === 'traffic'  && <TrafficAnalytics userId={userId} accessToken={accessToken} />}
      {tab === 'product'  && <ProductMetrics  userId={userId} accessToken={accessToken} />}
      {tab === 'stats'    && <StatsView       userId={userId} accessToken={accessToken} />}
      {tab === 'sessions' && <VisitorSessionsView userId={userId} accessToken={accessToken} />}
      {tab === 'feedback' && <FeedbackView    userId={userId} accessToken={accessToken} />}
    </div>
  );
}
